import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Search, X, ChevronLeft, ChevronRight, SlidersHorizontal, Package } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { UserSearchSelect } from "../ui/UserSearchSelect";

const STATUS_OPTIONS = [
    { value: "", label: "Tous" },
    { value: "new", label: "Nouveau" },
    { value: "open", label: "Ouvert" },
    { value: "in_progress", label: "En cours" },
    { value: "pending_user", label: "En attente" },
    { value: "resolved", label: "Résolu" },
    { value: "closed", label: "Clôturé" },
];
const PRIORITY_OPTIONS = [
    { value: "", label: "Toutes" },
    { value: "critical", label: "Critique" },
    { value: "high", label: "Haute" },
    { value: "medium", label: "Moyenne" },
    { value: "low", label: "Basse" },
];
const PERIOD_OPTIONS = [
    { value: "", label: "Tout" },
    { value: "7d", label: "7 jours" },
    { value: "30d", label: "30 jours" },
    { value: "3m", label: "3 mois" },
    { value: "1y", label: "1 an" },
];

const STATUS_COLORS = {
    new: "bg-purple-100 text-purple-700",
    draft: "bg-gray-100 text-gray-600",
    open: "bg-blue-100 text-blue-700",
    pending_approval: "bg-amber-100 text-amber-700",
    in_progress: "bg-cyan-100 text-cyan-700",
    pending_user: "bg-amber-100 text-amber-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-gray-100 text-gray-600",
    cancelled: "bg-red-100 text-red-600",
};
const PRIORITY_COLORS = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-yellow-100 text-yellow-700",
    low: "bg-green-100 text-green-700",
};
const MATCH_COLORS = {
    titre: "bg-blue-100 text-blue-700",
    description: "bg-gray-200 text-gray-700",
    commentaires: "bg-emerald-100 text-emerald-700",
    solutions: "bg-violet-100 text-violet-700",
    personnes: "bg-orange-100 text-orange-700",
    champs: "bg-cyan-100 text-cyan-700",
};

const STATUS_LABELS = { new: "Nouveau", draft: "Brouillon", open: "Ouvert", pending_approval: "En attente d'approbation", in_progress: "En cours", pending_user: "En attente", resolved: "Résolu", closed: "Clôturé", cancelled: "Annulé" };
const PRIORITY_LABELS = { critical: "Critique", high: "Haute", medium: "Moyenne", low: "Basse" };

export function TicketSearch() {
    const { currentUser, users, departments, getUserById, hasPermission } = useApp();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [query, setQuery] = useState(searchParams.get("q") || "");
    const [userId, setUserId] = useState(searchParams.get("user_id") || null);
    const [status, setStatus] = useState("");
    const [priority, setPriority] = useState("");
    const [deptFilter, setDeptFilter] = useState("");
    const [period, setPeriod] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [results, setResults] = useState(null);
    const [invResults, setInvResults] = useState([]);
    const [invPeople, setInvPeople] = useState({});
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const LIMIT = 20;
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    const canAccess = hasPermission("search");
    const isITStaff = currentUser?.role === "admin" || currentUser?.role === "agent";

    useEffect(() => {
        if (isITStaff) {
            api.getInventoryEmployees().then((r) => {
                const m = {};
                r.employees.forEach((p) => { m[p.id] = `${p.first_name} ${p.last_name}`; });
                setInvPeople(m);
            }).catch(() => {});
        }
    }, [isITStaff]);

    const doSearch = useCallback(async (q, uid, skip, st, pr, dept, per) => {
        if (!q && !uid) { setResults(null); setInvResults([]); return; }
        setLoading(true);
        if (isITStaff && q && q.length >= 2) {
            api.getInventoryItems({ search: q, limit: 10 }).then((r) => setInvResults(r.items)).catch(() => setInvResults([]));
        } else {
            setInvResults([]);
        }
        try {
            const params = { limit: LIMIT, skip };
            if (q && q.length >= 2) params.q = q;
            if (uid) params.user_id = uid;
            if (st) params.status = st;
            if (pr) params.priority = pr;
            if (dept) params.department_id = dept;
            if (per) params.period = per;
            if (!params.q && !params.user_id) { setResults(null); setLoading(false); return; }
            const data = await api.searchTicketsGlobal(params);
            setResults(data);
        } catch (e) {
            console.error("Search error:", e);
            setResults({ tickets: [], total: 0 });
        } finally {
            setLoading(false);
        }
    }, [isITStaff]);

    const triggerSearch = useCallback((newPage = 0) => {
        setPage(newPage);
        doSearch(query, userId, newPage * LIMIT, status, priority, deptFilter, period);
    }, [query, userId, status, priority, deptFilter, period, doSearch]);

    // Debounce text input
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (query.length >= 2 || userId) {
                triggerSearch(0);
            } else if (!query && !userId) {
                setResults(null);
            }
        }, 500);
        return () => clearTimeout(debounceRef.current);
    }, [query, userId]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Re-search when filters change
    useEffect(() => {
        if (query.length >= 2 || userId) triggerSearch(0);
    }, [status, priority, deptFilter, period]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Initial search from URL params
    useEffect(() => {
        const q = searchParams.get("q");
        if (q) {
            setQuery(q);
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            triggerSearch(0);
        }
    };

    const getUserName = (uid) => {
        if (!uid) return "-";
        const u = getUserById(uid);
        return u ? `${u.first_name} ${u.last_name}` : uid;
    };

    const selectedUser = userId ? getUserById(userId) : null;

    if (!canAccess) {
        return (
            <div className="page-container" data-testid="search-access-denied">
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Search className="w-12 h-12 mb-3 opacity-40" />
                    <p className="text-sm">{"Accès réservé aux agents IT et coordinateurs."}</p>
                </div>
            </div>
        );
    }

    const totalPages = results ? Math.ceil(results.total / LIMIT) : 0;

    return (
        <div className="page-container" data-testid="ticket-search-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="page-title">Recherche globale</h1>
                    <p className="page-subtitle text-sm text-gray-500">Recherchez dans tous les tickets, commentaires, solutions et champs dynamiques</p>
                </div>
            </div>

            {/* Search bar */}
            <div className="card mb-4">
                <div className="card-body space-y-3">
                    <div className="flex gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Rechercher tickets, Asset Tag, N° série..."
                                className="pl-10 h-11"
                                data-testid="global-search-input"
                            />
                            {query && (
                                <button onClick={() => { setQuery(""); setResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <Button onClick={() => triggerSearch(0)} disabled={loading} data-testid="search-submit-btn" className="h-11 px-5">
                            {loading ? "..." : "Rechercher"}
                        </Button>
                        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={`h-11 ${showFilters ? "bg-gray-100" : ""}`} data-testid="toggle-filters-btn">
                            <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Filtres
                        </Button>
                    </div>

                    {/* User filter */}
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Filtrer par utilisateur :</span>
                        <div className="flex-1 max-w-md">
                            <UserSearchSelect
                                users={users.filter((u) => u.is_active !== false)}
                                departments={departments}
                                value={userId}
                                onChange={(id) => { setUserId(id); setPage(0); }}
                                placeholder="Tout voir pour un utilisateur..."
                                data-testid="search-user-filter"
                            />
                        </div>
                        {userId && (
                            <button onClick={() => { setUserId(null); setPage(0); }} className="text-xs text-gray-400 hover:text-red-500">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Additional filters */}
                    {showFilters && (
                        <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100" data-testid="search-filters">
                            <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border rounded-md px-2 py-1.5 bg-white" data-testid="filter-status">
                                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="text-xs border rounded-md px-2 py-1.5 bg-white" data-testid="filter-priority">
                                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="text-xs border rounded-md px-2 py-1.5 bg-white" data-testid="filter-department">
                                <option value="">Tous les d{"é"}partements</option>
                                {departments.filter((d) => !d.deleted_at).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="text-xs border rounded-md px-2 py-1.5 bg-white" data-testid="filter-period">
                                {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {(status || priority || deptFilter || period) && (
                                <button onClick={() => { setStatus(""); setPriority(""); setDeptFilter(""); setPeriod(""); }} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
                                    <X className="w-3 h-3" /> R{"é"}initialiser
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* User summary */}
            {userId && selectedUser && results && (
                <div className="card mb-4" data-testid="user-summary">
                    <div className="card-body py-3">
                        <p className="text-sm text-gray-700">
                            <span className="font-semibold">{selectedUser.first_name} {selectedUser.last_name}</span>
                            {" \u2014 "}
                            <span className="text-gray-500">{results.total} ticket(s) {"lié"}(s)</span>
                        </p>
                    </div>
                </div>
            )}

            {/* Inventory results */}
            {invResults.length > 0 && !loading && (
                <div className="card mb-4" data-testid="inventory-search-results">
                    <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Matériel (Inventaire) — {invResults.length} résultat(s)</h3></div>
                    <div className="card-body p-0">
                        <div className="divide-y divide-gray-50">
                            {invResults.map((it) => (
                                <div key={it.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/inventory/${it.id}`)} data-testid={`inv-search-result-${it.id}`}>
                                    <Package className="w-4 h-4 text-teal-600 flex-shrink-0" />
                                    <span className="font-mono text-sm font-semibold text-gray-900">{it.asset_tag || it.name}</span>
                                    <span className="text-xs text-gray-500 truncate">{[it.name && it.asset_tag ? it.name : "", it.brand, it.model].filter(Boolean).join(" ")}</span>
                                    {it.serial_number && <span className="text-[10px] font-mono text-gray-400">SN: {it.serial_number}</span>}
                                    <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
                                        {it.assigned_to_user_id ? (invPeople[it.assigned_to_user_id] || "Assigné") : "En stock"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Results */}
            {!results && !loading && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-testid="search-empty-state">
                    <Search className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm text-center max-w-md">
                        {"Recherchez par mot-clé, numéro de ticket, ou sélectionnez un utilisateur pour voir tout son historique."}
                    </p>
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                </div>
            )}

            {results && !loading && (
                <div data-testid="search-results">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-medium text-gray-500" data-testid="search-results-count">
                            {results.total} {"ré"}sultat(s)
                            {query && <span className="ml-1 text-gray-400">pour &laquo;{query}&raquo;</span>}
                        </p>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => triggerSearch(page - 1)} data-testid="prev-page">
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </Button>
                                <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
                                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => triggerSearch(page + 1)} data-testid="next-page">
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        )}
                    </div>

                    {results.tickets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400" data-testid="search-no-results">
                            <Search className="w-8 h-8 mb-2 opacity-30" />
                            <p className="text-sm">Aucun ticket {"trouvé"} pour &laquo;{query}&raquo;.</p>
                        </div>
                    ) : (
                        <div className="card">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="search-results-table">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-left">
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">#</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Titre</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Statut</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">{"Priorité"}</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Demandeur</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Date</th>
                                            <th className="px-4 py-2.5 text-xs font-medium text-gray-500">{"Trouvé"} dans</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.tickets.map((t) => (
                                            <tr
                                                key={t.id}
                                                className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => navigate(`/tickets/${t.id}`)}
                                                data-testid={`search-result-${t.id}`}
                                            >
                                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{t.ticket_number}</td>
                                                <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{t.title}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status] || "bg-gray-100 text-gray-600"}`}>
                                                        {STATUS_LABELS[t.status] || t.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[t.priority] || "bg-gray-100 text-gray-600"}`}>
                                                        {PRIORITY_LABELS[t.priority] || t.priority}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{getUserName(t.requester_id)}</td>
                                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                                    {t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : "-"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {(t.match_locations || []).map((loc) => (
                                                            <span key={loc} className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${MATCH_COLORS[loc] || "bg-gray-100 text-gray-500"}`}>
                                                                {loc}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Bottom pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => triggerSearch(page - 1)}>
                                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {"Précédent"}
                            </Button>
                            <span className="text-xs text-gray-500">Page {page + 1} sur {totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => triggerSearch(page + 1)}>
                                Suivant <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
